/**
 * Where a seat is remembered, so the player can sit back down in it.
 *
 * Two places, for two different ways of coming back:
 *
 *   - **the tab** (`sessionStorage`) — a reload. The claim is this tab's own,
 *     so it is offered as `rejoin`, which takes the seat even from a socket the
 *     server has not noticed is dead yet.
 *   - **the browser** (`localStorage`, one entry per room) — a tab that was
 *     closed and opened again inside the resume grace. `sessionStorage` died
 *     with the tab, so without this the reopened tab was a stranger: it went
 *     through every gate again and could not have its own held seat, which the
 *     partner's banner had just promised it. It is offered as `reclaim`, which
 *     only takes a seat nobody is sitting in — the browser's memory is shared by
 *     every tab in it, and two testers on one laptop must not be able to pull
 *     each other out of their chairs.
 *
 * Pure apart from the two stores, which are passed in so a test can hand it
 * maps. Every read and write is guarded: a profile that refuses storage loses
 * the way back in and nothing else.
 */

import type { PlayerId } from '@overcooked/shared'

export interface SeatClaim {
  roomCode: string
  playerId: PlayerId
  resumeToken: string
}

/** The slice of the Web Storage API this needs. */
export interface ClaimStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/** The tab's one claim, kept under the key it has always had. */
export const TAB_KEY = 'overcooked.resume'

/** The browser's claim for one room. */
export const browserKey = (roomCode: string): string => `overcooked.seat.${roomCode.toUpperCase()}`

/**
 * How long the browser keeps a claim: the life of the resume token it carries
 * (`RESUME_TOKEN_TTL_MS` on the server). The server's grace window is what
 * actually bounds a return; this only stops a dead claim lingering in storage
 * for a room that has long since closed.
 */
export const BROWSER_CLAIM_TTL_MS = 60 * 60 * 1000

function parse(raw: string | null): (SeatClaim & { savedAt?: number }) | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<SeatClaim & { savedAt: number }>
    if (
      typeof parsed.roomCode !== 'string' ||
      typeof parsed.playerId !== 'string' ||
      typeof parsed.resumeToken !== 'string' ||
      !parsed.roomCode ||
      !parsed.playerId ||
      !parsed.resumeToken
    ) {
      return null
    }
    return parsed as SeatClaim & { savedAt?: number }
  } catch {
    return null
  }
}

function read(store: ClaimStore | null, key: string): (SeatClaim & { savedAt?: number }) | null {
  if (!store) return null
  try {
    return parse(store.getItem(key))
  } catch {
    return null
  }
}

function write(store: ClaimStore | null, key: string, value: unknown): void {
  if (!store) return
  try {
    store.setItem(key, JSON.stringify(value))
  } catch {
    /* storage refused: see the module comment */
  }
}

function remove(store: ClaimStore | null, key: string): void {
  if (!store) return
  try {
    store.removeItem(key)
  } catch {
    /* storage refused: see the module comment */
  }
}

export interface ClaimChoice {
  /** `rejoin` for the tab's own claim, `reclaim` for the browser's. */
  via: 'rejoin' | 'reclaim'
  claim: SeatClaim
}

/**
 * Which claim, if any, this page should make on `roomCode` when it connects.
 *
 * The tab's own claim wins: it is the stronger one and the only one that may
 * displace a live socket. A claim for a different room is never offered — a tab
 * that walks into `/r/WXYZ` must not carry a claim on `ABCD` in with it.
 */
export function chooseClaim(
  roomCode: string | null | undefined,
  tab: ClaimStore | null,
  browser: ClaimStore | null,
  now: number = Date.now(),
): ClaimChoice | null {
  if (!roomCode) return null
  const code = roomCode.toUpperCase()
  const own = read(tab, TAB_KEY)
  if (own && own.roomCode.toUpperCase() === code) {
    return { via: 'rejoin', claim: { roomCode: code, playerId: own.playerId, resumeToken: own.resumeToken } }
  }
  const remembered = read(browser, browserKey(code))
  if (!remembered || remembered.roomCode.toUpperCase() !== code) return null
  if (typeof remembered.savedAt === 'number' && now - remembered.savedAt > BROWSER_CLAIM_TTL_MS) {
    remove(browser, browserKey(code))
    return null
  }
  return {
    via: 'reclaim',
    claim: { roomCode: code, playerId: remembered.playerId, resumeToken: remembered.resumeToken },
  }
}

/** Whether this page could sit back down in `roomCode` without a free seat. */
export function holdsClaim(
  roomCode: string,
  tab: ClaimStore | null,
  browser: ClaimStore | null,
  now: number = Date.now(),
): boolean {
  return chooseClaim(roomCode, tab, browser, now) !== null
}

/** Remember a seat in both places. Called on every `joined`: the token is re-minted each time. */
export function rememberSeat(
  claim: SeatClaim,
  tab: ClaimStore | null,
  browser: ClaimStore | null,
  now: number = Date.now(),
): void {
  write(tab, TAB_KEY, claim)
  write(browser, browserKey(claim.roomCode), { ...claim, savedAt: now })
}

/**
 * Forget a seat. The tab's claim always goes; the browser's goes too unless
 * `keepBrowser` — which is for a reclaim refused because another tab in this
 * browser is sitting in the seat, and still needs the claim for its own reload.
 */
export function forgetSeat(
  roomCode: string | null | undefined,
  tab: ClaimStore | null,
  browser: ClaimStore | null,
  options: { keepBrowser?: boolean } = {},
): void {
  const own = read(tab, TAB_KEY)
  if (!roomCode || !own || own.roomCode.toUpperCase() === roomCode.toUpperCase()) remove(tab, TAB_KEY)
  if (roomCode && !options.keepBrowser) remove(browser, browserKey(roomCode))
}

/** The server's word for a reclaim of a seat that is in use, from `socket.ts`. */
export function refusedAsInUse(message: string): boolean {
  return /in use in another tab/.test(message)
}

/** The two real stores, or `null` where the environment has none or refuses them. */
export function tabStore(): ClaimStore | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

export function browserStore(): ClaimStore | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}
